import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@byscott-io/corebyscott';
import { useParams, Link } from 'react-router-dom';
import { apps, features } from '../api/client';

export default function FeaturesPage() {
  const { appId } = useParams();
  const [app, setApp] = useState(null);
  const [featureList, setFeatureList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [appRes, featuresRes] = await Promise.all([
        apps.get(appId),
        features.list(appId),
      ]);
      setApp(appRes.data.app);
      setFeatureList(featuresRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (feature) => {
    if (!confirm(`Delete feature "${feature.name}"?`)) return;
    try {
      await features.delete(appId, feature.id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete feature');
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading...</div>;
  if (error) return <div className="p-6"><div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div></div>;

  return (
    <div className="p-6">
      <div className="mb-6 text-sm">
        <Link to="/app/apps" className="text-blue-600 hover:text-blue-800">Apps</Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to={`/app/apps/${appId}`} className="text-blue-600 hover:text-blue-800">{app?.name}</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-600">Features</span>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Features</h1>
        <button onClick={() => setShowCreateModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
          + New Feature
        </button>
      </div>

      <p className="text-gray-600 mb-6">Features are boolean flags that can be enabled/disabled per pricing level.</p>

      {featureList.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <p className="text-gray-500 mb-4">No features defined yet.</p>
          <button onClick={() => setShowCreateModal(true)} className="text-blue-600 hover:text-blue-800">Create a feature</button>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
          {featureList.map((feature) => (
            <div key={feature.id} className="p-4 flex justify-between items-center">
              <div>
                <p className="font-medium">{feature.name}</p>
                <p className="text-sm text-gray-500">{feature.key}</p>
                {feature.description && <p className="text-sm text-gray-600 mt-1">{feature.description}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditingFeature(feature)} className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                <button onClick={() => handleDelete(feature)} className="text-red-600 hover:text-red-800 text-sm">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <FeatureModal appId={appId} onClose={() => setShowCreateModal(false)} onSaved={() => { setShowCreateModal(false); loadData(); }} />
      )}

      {editingFeature && (
        <FeatureModal appId={appId} feature={editingFeature} onClose={() => setEditingFeature(null)} onSaved={() => { setEditingFeature(null); loadData(); }} />
      )}
    </div>
  );
}

function FeatureModal({ appId, feature, onClose, onSaved }) {
  const [key, setKey] = useState(feature?.key || '');
  const [name, setName] = useState(feature?.name || '');
  const [description, setDescription] = useState(feature?.description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      if (feature) {
        await features.update(appId, feature.id, { key, name, description });
      } else {
        await features.create(appId, { key, name, description });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save feature');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={feature ? 'Edit Feature' : 'New Feature'} size="md">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
          <input type="text" value={key} onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="e.g., advanced_reporting" required />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="e.g., Advanced Reporting" required />
        </div>
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" rows={2} />
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button type="submit" disabled={saving || !key || !name} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : (feature ? 'Save' : 'Create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
